import React, { useState } from "react";
import { Alert, Button, Card, Checkbox, Input, List, Modal, Space, Tag, Typography } from "antd";
import { AudioOutlined, LoadingOutlined, ReadOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Error as ErrorMessage } from "../../../commons/Messages/Messages";
import useDictation from "./useDictation";
import { parseDictation } from "./api";
import { formatLength, formatM2 } from "./format";
import { voiceLanguageLabel } from "../profile/voiceLanguages";

const SHAPE_LABELS = {
  rectangle: "Rectangle",
  quad: "4 walls + diagonal",
  polygon: "Free shape"
};

const dimensionsText = (room, lng) => {
  if (room.shape === "rectangle") {
    return `${formatLength(room.length, lng)} × ${formatLength(room.width, lng)}`;
  }
  if (room.shape === "quad") return room.sides.map(side => formatLength(side, lng)).join(" · ");
  return room.walls.map(wall => formatLength(wall.length, lng)).join(" · ");
};

/**
 * What was said or typed on site. Speaking writes into the text; "Fill in rooms" reads the
 * text and, after a review, adds the rooms (or completes rooms with the same name).
 */
const DictationBox = ({
  value,
  onChange,
  voiceConfigured,
  voiceTranslateTo,
  rules,
  onApply,
  readOnly
}) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState([]);

  const append = text => {
    if (!text) return;
    onChange(prev => {
      const current = prev || "";
      const glue = !current || /\s$/.test(current) ? "" : " ";
      return `${current}${glue}${text}`;
    });
  };

  const dictation = useDictation({
    language: lng,
    serverAvailable: voiceConfigured,
    translate: Boolean(voiceTranslateTo),
    onText: append,
    onError: (code, err) => {
      if (code === "server") return generalErrorAlert(err);
      const messages = {
        microphone: "Please allow access to the microphone.",
        empty: "Nothing was understood. Please try again.",
        unsupported: "Voice input is not available on this device.",
        live: "Speech recognition stopped. Please try again."
      };
      ErrorMessage(t(messages[code] || messages.live));
    }
  });

  const readText = async () => {
    setReading(true);
    try {
      const res = await parseDictation(value, lng, rules);
      if (!res.data.rooms.length) {
        ErrorMessage(t("No rooms with dimensions were found in the text."));
        return;
      }
      setResult(res.data);
      setSelected(res.data.rooms.map((_, index) => index));
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setReading(false);
    }
  };

  const apply = () => {
    onApply(result.rooms.filter((_, index) => selected.includes(index)));
    setResult(null);
  };

  const active = dictation.status === "listening" || dictation.status === "recording";
  const translateLabel = voiceLanguageLabel(voiceTranslateTo);
  const translating = Boolean(translateLabel) && dictation.mode === "recording";
  const seconds = Math.floor(dictation.elapsed / 1000);

  return (
    <Card
      size="small"
      className="ms-dictation"
      title={
        <Space size={6} wrap>
          {t("Dictate or type")}
          {translateLabel && (
            <Tag color={translating ? "blue" : "default"} className="ms-small">
              {translating
                ? `${t("Translated into")} ${t(translateLabel)}`
                : t("Translation needs the server assistant")}
            </Tag>
          )}
        </Space>
      }
      extra={
        !readOnly &&
        dictation.available && (
          <Button
            type={active ? "primary" : "default"}
            danger={active}
            shape="round"
            className={active ? "ms-mic ms-mic--active" : "ms-mic"}
            icon={
              dictation.status === "processing" || dictation.status === "starting" ? (
                <LoadingOutlined />
              ) : active ? (
                <StopOutlined />
              ) : (
                <AudioOutlined />
              )
            }
            disabled={dictation.status === "processing" || dictation.status === "starting"}
            onClick={active ? dictation.stop : dictation.start}
          >
            {dictation.status === "processing" ? t("Writing…") : active ? t("Stop") : t("Speak")}
          </Button>
        )
      }
    >
      <Input.TextArea
        value={value}
        readOnly={readOnly}
        onChange={event => onChange(event.target.value)}
        autoSize={{ minRows: 3, maxRows: 12 }}
        placeholder={t(
          'e.g. "Living room 4.20 by 3.80, height 2.50, two windows 1.20 by 1.40, door 0.885 by 2.01"'
        )}
      />
      {dictation.status === "listening" && (
        <Typography.Paragraph type="secondary" className="ms-interim">
          <span className="ms-rec-dot" /> {dictation.interim || t("Listening…")}
        </Typography.Paragraph>
      )}
      {dictation.status === "recording" && (
        <Typography.Paragraph type="secondary" className="ms-interim">
          <span className="ms-rec-dot" /> {t("Recording")} {Math.floor(seconds / 60)}:
          {String(seconds % 60).padStart(2, "0")} · {t("The text appears when you stop.")}
        </Typography.Paragraph>
      )}
      {!readOnly && (
        <Space wrap className="ms-dictation-actions">
          {voiceConfigured ? (
            <Button
              icon={<ReadOutlined />}
              loading={reading}
              disabled={!value?.trim() || active}
              onClick={readText}
            >
              {t("Fill in rooms from text")}
            </Button>
          ) : (
            <Typography.Text type="secondary">
              {t("Filling in rooms from text is not set up on this server.")}
            </Typography.Text>
          )}
        </Space>
      )}

      <Modal
        open={Boolean(result)}
        title={t("Check the rooms found")}
        onCancel={() => setResult(null)}
        onOk={apply}
        okText={t("Add to measurement")}
        cancelText={t("Cancel")}
        okButtonProps={{ disabled: !selected.length }}
        width={640}
      >
        {result && (
          <>
            <Typography.Paragraph type="secondary">
              {t(
                "Rooms with a name that already exists are completed; the others are added. Please check every number."
              )}
            </Typography.Paragraph>
            <List
              dataSource={result.rooms}
              renderItem={(room, index) => {
                const summary = result.summary.rooms[index];
                return (
                  <List.Item>
                    <Checkbox
                      checked={selected.includes(index)}
                      onChange={event =>
                        setSelected(prev =>
                          event.target.checked
                            ? [...prev, index]
                            : prev.filter(item => item !== index)
                        )
                      }
                    >
                      <div className="ms-found-room">
                        <Typography.Text strong>
                          {room.name || t("Room")} {room.level && `· ${room.level}`}
                        </Typography.Text>
                        <div>
                          <Tag>{t(SHAPE_LABELS[room.shape])}</Tag>
                          {dimensionsText(room, lng)}
                          {room.height ? ` · ${t("Height")} ${formatLength(room.height, lng)}` : ""}
                        </div>
                        {room.openings.length > 0 && (
                          <div className="ms-small">
                            {t("Openings")}: {room.openings.reduce((sum, o) => sum + o.count, 0)}
                          </div>
                        )}
                        {summary?.valid && (
                          <div className="ms-small">
                            {t("Floor")} {formatM2(summary.figures.floorNet, lng)}
                            {room.height
                              ? ` · ${t("Walls")} ${formatM2(summary.figures.wallNet, lng)}`
                              : ""}
                          </div>
                        )}
                      </div>
                    </Checkbox>
                  </List.Item>
                );
              }}
            />
            {result.unclear.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={t("Not assigned")}
                description={
                  <ul className="ms-unclear">
                    {result.unclear.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        )}
      </Modal>
    </Card>
  );
};

export default DictationBox;
